import { useEffect, useState } from "react";
import {
  Modal,
  TextInput,
  PasswordInput,
  Select,
  Button,
  Group,
  Stack,
} from "@mantine/core";
import { AdminRole, ADMIN_ROLE_LABELS, type Admin } from "../../types/admin";
import type { CreateAdminBody } from "../../api/admins";

const ROLE_OPTIONS = Object.values(AdminRole).map((r) => ({
  value: r,
  label: ADMIN_ROLE_LABELS[r],
}));

export interface AdminFormValues {
  name: string;
  email: string;
  phoneNumber: string;
  password: string;
  role: AdminRole;
}

export function AdminFormModal({
  opened,
  onClose,
  editing,
  loading,
  onSubmit,
}: {
  opened: boolean;
  onClose: () => void;
  editing: Admin | null;
  loading: boolean;
  onSubmit: (values: AdminFormValues) => void;
}) {
  const [v, setV] = useState<AdminFormValues>({
    name: "",
    email: "",
    phoneNumber: "",
    password: "",
    role: AdminRole.OPERATIONS_TEAM,
  });

  useEffect(() => {
    if (opened) {
      setV({
        name: editing?.name ?? "",
        email: editing?.email ?? "",
        phoneNumber: editing?.phoneNumber ?? "",
        password: "",
        role: editing?.role ?? AdminRole.OPERATIONS_TEAM,
      });
    }
  }, [opened, editing]);

  const isEdit = !!editing;
  const valid =
    v.name.trim() &&
    /\S+@\S+\.\S+/.test(v.email) &&
    v.phoneNumber.trim() &&
    v.role &&
    (isEdit || v.password.length >= 6);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? "Edit admin" : "New admin"}
      centered
    >
      <Stack>
        <TextInput
          label="Full name"
          value={v.name}
          onChange={(e) => setV({ ...v, name: e.currentTarget.value })}
          required
        />
        <TextInput
          label="Email"
          type="email"
          value={v.email}
          onChange={(e) => setV({ ...v, email: e.currentTarget.value })}
          required
        />
        <TextInput
          label="Phone number"
          placeholder="+255…"
          value={v.phoneNumber}
          onChange={(e) => setV({ ...v, phoneNumber: e.currentTarget.value })}
          required
        />
        <Select
          label="Role"
          data={ROLE_OPTIONS}
          value={v.role}
          onChange={(val) =>
            setV({ ...v, role: (val as AdminRole) ?? AdminRole.OPERATIONS_TEAM })
          }
          allowDeselect={false}
          required
        />
        {!isEdit && (
          <PasswordInput
            label="Temporary password"
            description="At least 6 characters. The admin can change it after first login."
            value={v.password}
            onChange={(e) => setV({ ...v, password: e.currentTarget.value })}
            required
          />
        )}
        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={loading}
            disabled={!valid}
            onClick={() => onSubmit(v)}
          >
            {isEdit ? "Save changes" : "Create admin"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// Narrow the create body (drops empty password on edit at the call site).
export type { CreateAdminBody };
